/**
 * Bot player implementations.
 * Each bot implements the IBotPlayer interface with a specific action strategy.
 */

import type { BattleAction, BotEngineView, IBotPlayer, PlayerId, Team, TrainerProfile } from "./types";

/**
 * Picks a random legal move, or a random legal switch if no moves are available,
 * or struggle as a last resort.
 */
export function pickRandomLegalAction(engine: BotEngineView, player: PlayerId): BattleAction {
  const legal = engine.getLegalActions(player);
  const moves = legal.filter((a) => a.type === "move");
  if (moves.length > 0) return moves[Math.floor(Math.random() * moves.length)];
  return legal[Math.floor(Math.random() * legal.length)] ?? { type: "struggle" };
}

/** A bot that always picks a random legal action each turn (prefers moves). */
export const firstLegalBot: IBotPlayer = {
  id: "first-legal",
  name: "First Legal",
  profile: {
    name: "Bot",
    backSprite: "bot",
    frontSprite: "bot",
    vsSprite: "bot",
    isCustom: false,
  },
  chooseAction(engine: BotEngineView, side: PlayerId): BattleAction {
    return pickRandomLegalAction(engine, side);
  },
};

/** A bot that picks a random legal action each turn. */
export const randomBot: IBotPlayer = {
  id: "random",
  name: "Random",
  profile: {
    name: "Bot",
    backSprite: "bot",
    frontSprite: "bot",
    vsSprite: "bot",
    isCustom: false,
  },
  chooseAction(engine: BotEngineView, side: PlayerId): BattleAction {
    const legal = engine.getLegalActions(side);
    const idx = Math.floor(Math.random() * legal.length);
    return legal[idx] ?? { type: "struggle" };
  },
};

/**
 * A bot that uses a fixed team and profile.
 * Great for gym leader / trainer presets.
 */
export function createPremadeTeamBot(
  id: string,
  name: string,
  profile: TrainerProfile,
  team: Team,
  strategy: "first-legal" | "random" = "first-legal",
): IBotPlayer {
  const actionFn = strategy === "random" ? randomBot.chooseAction : firstLegalBot.chooseAction;
  return {
    id,
    name,
    profile,
    chooseAction: actionFn,
    buildTeam: () => team,
  };
}

/**
 * Factory that combines an action strategy, an optional team builder, and a profile.
 * If no team builder is provided, the bot will need a team supplied externally.
 */
export function createBotPlayer(
  id: string,
  name: string,
  profile: TrainerProfile,
  actionStrategy: "first-legal" | "random",
  teamBuilder?: () => Promise<Team> | Team,
): IBotPlayer {
  const actionFn = actionStrategy === "random" ? randomBot.chooseAction : firstLegalBot.chooseAction;
  return {
    id,
    name,
    profile,
    chooseAction: actionFn,
    ...(teamBuilder ? { buildTeam: teamBuilder } : {}),
  };
}

/** Array of built-in bot presets that can be selected from the lobby. */
export const BUILT_IN_BOTS: IBotPlayer[] = [firstLegalBot, randomBot];